/**
 * Math Symbol Provider
 * Provides math symbol completions in math mode
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { PositionInfo, LaTeXContext } from '../types'
import { CompletionProvider } from './baseProvider'
import { getUnicodeMathSymbols } from '@/utils/latexDataLoader'
import { logDebug } from '@utils/logger'

export class MathSymbolProvider implements CompletionProvider {
  name = 'mathSymbol'
  priority = 70 // Lower priority - only in math mode

  canHandle(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): boolean {
    // Only provide in math mode
    return latexContext.inMathMode && (position.isInCommand || position.isAfterBackslash)
  }

  provide(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): CompletionResult | null {
    const { state, pos } = context
    const text = state.doc.toString()
    
    // Find backslash position
    let backslashPos = -1
    for (let i = pos - 1; i >= 0 && (pos - i) <= 50; i--) {
      if (text[i] === '\\' && (i === 0 || text[i - 1] !== '\\')) {
        backslashPos = i
        break
      }
      if (text[i] === ' ' || text[i] === '\n' || text[i] === '\t') {
        break
      }
    }
    
    if (backslashPos < 0) return null
    
    const query = text.substring(backslashPos + 1, pos).toLowerCase()
    const symbols = getUnicodeMathSymbols()
    
    // Filter symbols matching query
    const matches = symbols
      .filter(symbol => {
        const cmdName = symbol.command.toLowerCase()
        return cmdName.startsWith(query) || cmdName.includes(query)
      })
      .slice(0, 30)
    
    if (matches.length === 0) return null
    
    const options = matches.map(symbol => ({
      label: symbol.command,
      type: 'variable' as const,
      info: symbol.detail || symbol.documentation || `Math symbol: ${symbol.command}`,
      apply: `\\${symbol.command}`
    }))
    
    logDebug('[MathSymbolProvider] Providing completions', {
      query,
      optionsCount: options.length
    })
    
    return {
      from: backslashPos,
      to: pos,
      options,
      filter: false,
      span: /^\\[a-zA-Z*]*$/,
      validFor: /^\\[a-zA-Z*]*$/
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

